import {useState} from 'react';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Label,
} from '@tinct/web-components';

interface LaunchCampaignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyNames: string[];
  readyCount: number;
  isPublishing: boolean;
  onLaunch: () => void;
  /** ADWORD campaigns: names are ads campaigns, and a zero-variant launch is fine. */
  isAdwordAxis?: boolean;
}

const buildAudienceSentence = (
  companyNames: string[],
  readyCount: number,
  isAdwordAxis: boolean,
) => {
  if (isAdwordAxis && readyCount === 0) {
    return 'Your campaign goes live now — variants publish as your ads campaigns (Google Ads / LinkedIn Ads) match.';
  }

  const featured = companyNames.slice(0, 3);
  const remaining = Math.max(readyCount - featured.length, 0);
  const noun = isAdwordAxis ? 'ads campaign' : 'company';
  const nounPlural = isAdwordAxis ? 'ads campaigns' : 'companies';

  if (featured.length === 0) {
    return `Visitors from ${readyCount} ${
      readyCount === 1 ? noun : nounPlural
    } will see personalized content.`;
  }

  const featuredList = featured.join(', ');
  const fromList = isAdwordAxis
    ? `from your ads campaigns ${featuredList}`
    : `from ${featuredList}`;
  if (remaining === 0) {
    return `Visitors ${fromList} will see personalized content.`;
  }
  return `Visitors ${fromList} and ${remaining} other ${
    remaining === 1 ? noun : nounPlural
  } will see personalized content.`;
};

export const LaunchCampaignModal = ({
  open,
  onOpenChange,
  companyNames,
  readyCount,
  isPublishing,
  onLaunch,
  isAdwordAxis = false,
}: LaunchCampaignModalProps) => {
  const assetsUrl = useStaticAssetsUrl();
  const [confirmed, setConfirmed] = useState(false);

  const handleOpenChange = (value: boolean) => {
    onOpenChange(value);
    if (!value) {
      setConfirmed(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton
        className="max-w-md gap-0 overflow-hidden rounded-3xl p-0"
      >
        {/* Cover image */}
        <div className="px-2 pt-2">
          <div className="overflow-hidden rounded-2xl">
            <img
              src={`${assetsUrl}/launch-campaign.png`}
              alt=""
              className="aspect-[2/1] w-full object-cover"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-3 p-8 text-center">
          <DialogTitle className="font-heading text-2xl font-semibold tracking-[-0.4px] text-foreground">
            Ready to launch?
          </DialogTitle>
          <p className="text-base text-muted-foreground">
            {buildAudienceSentence(companyNames, readyCount, isAdwordAxis)}
          </p>
        </div>

        {/* Checkbox confirmation */}
        <div className="flex items-start gap-2 px-8 pb-6">
          <Checkbox
            id="confirm-launch"
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
          />
          <Label
            htmlFor="confirm-launch"
            className="cursor-pointer text-sm font-medium leading-none text-foreground"
          >
            {isAdwordAxis && readyCount === 0
              ? 'I understand variants will be added as my ads campaigns match.'
              : "I've previewed my pages and yes they are ready !"}
          </Label>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row gap-2 px-8 pb-8">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => handleOpenChange(false)}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="flex-1"
            onClick={onLaunch}
            disabled={isPublishing || !confirmed}
          >
            {isPublishing ? 'Launching...' : 'Launch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
